import {createPortalApiClient} from '@services/apiClient';
import type {
    AusentismoSeriesResponse,
    CertificadoAptitudItem,
    CertificadosListParams,
    CertificadosListResponse,
    DashboardResumen,
    EmpleadosListParams,
    EmpleadosListResponse,
    EstudiosParaclinicosParams,
    EstudiosParaclinicosResponse,
    ExpedienteLaboralResponse,
    NotificacionesPortalResponse
} from '../types';
import type {PortalPerfil, PortalPerfilUpdatePayload} from '../types/perfil';
import {normalizeDashboardResumen} from '../utils/dashboard';
import {normalizeDictamenAptitud, toApiDictamen} from '../utils/dictamen';
import type {
    DictamenActual,
    EstudioParaclinico,
    EventoExpediente,
    NotificacionPortal
} from '../types/portalCliente';
import type {Empleado, UltimoCertificado} from '../types/empleado';

const portalAPI = createPortalApiClient();

const extractCertificadoIdFromPath = (ruta?: string | null): string | null => {
    if (!ruta) {
        return null;
    }

    const match = String(ruta).match(/certificados-aptitud\/([^/]+)\/download/i);
    return match?.[1] || null;
};

const normalizeUltimoCertificado = (raw: unknown): UltimoCertificado | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const cert = raw as Record<string, unknown>;
    const estatus = normalizeDictamenAptitud(
        cert.estatus_aptitud ?? cert.dictamen ?? cert.dictamen_actual
    );

    return {
        id: String(cert.id),
        fecha_emision: String(cert.fecha_emision ?? ''),
        fecha_vencimiento:
            (cert.fecha_vencimiento as string | null | undefined) ??
            (cert.vigente_hasta as string | null | undefined) ??
            null,
        estatus_aptitud: estatus as UltimoCertificado['estatus_aptitud'],
        etiqueta_aptitud: String(
            cert.etiqueta_aptitud ?? cert.etiqueta ?? estatus.replace(/_/g, ' ')
        ),
        descargable: Boolean(cert.descargable),
        url_descarga: (cert.url_descarga as string | null | undefined) ?? null
    };
};

const normalizeEmpleado = (raw: unknown): Empleado => {
    const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

    return {
        id: String(row.id ?? ''),
        numero_empleado:
            (row.numero_empleado as string | null | undefined) ??
            (row.id_empleado_empresa as string | null | undefined) ??
            null,
        nombre: String(row.nombre ?? ''),
        apellidos: String(row.apellidos ?? ''),
        puesto: (row.puesto as string | null | undefined) ?? null,
        departamento:
            (row.departamento as string | null | undefined) ??
            (row.area_departamento as string | null | undefined) ??
            null,
        estatus_laboral: (row.estatus_laboral as Empleado['estatus_laboral']) || 'activo',
        fecha_ingreso: (row.fecha_ingreso as string | null | undefined) ?? null,
        dictamen_actual: (row.dictamen_actual as Empleado['dictamen_actual']) ?? null,
        ultimo_certificado: normalizeUltimoCertificado(row.ultimo_certificado),
        proximo_examen_periodico:
            (row.proximo_examen_periodico as string | null | undefined) ?? null,
        tiene_restriccion_activa: Boolean(row.tiene_restriccion_activa)
    };
};

const normalizeCertificado = (raw: unknown): CertificadoAptitudItem => {
    const cert = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const empleadoRaw =
        cert.empleado && typeof cert.empleado === 'object'
            ? (cert.empleado as Record<string, unknown>)
            : null;
    const estatus = normalizeDictamenAptitud(cert.dictamen ?? cert.estatus_aptitud);

    return {
        id: String(cert.id ?? ''),
        folio: (cert.folio as string | null | undefined) ?? null,
        tipo_examen: (cert.tipo_examen as string | null | undefined) ?? null,
        fecha_emision: String(cert.fecha_emision ?? ''),
        dictamen: String(cert.dictamen ?? estatus),
        estatus_aptitud: estatus,
        restricciones_laborales:
            (cert.restricciones_laborales as string | null | undefined) ?? null,
        descargable: Boolean(cert.descargable),
        empleado: empleadoRaw
            ? {
                  id: String(empleadoRaw.id ?? ''),
                  nombre: String(empleadoRaw.nombre ?? ''),
                  apellidos: String(empleadoRaw.apellidos ?? ''),
                  numero_empleado:
                      (empleadoRaw.numero_empleado as string | null | undefined) ??
                      (empleadoRaw.id_empleado_empresa as string | null | undefined) ??
                      null,
                  puesto: (empleadoRaw.puesto as string | null | undefined) ?? null
              }
            : null
    };
};

const normalizeDictamenActual = (raw: unknown): DictamenActual => {
    if (!raw || typeof raw !== 'object') {
        const estatus = normalizeDictamenAptitud(raw);
        return {
            estatus,
            etiqueta: estatus.replace(/_/g, ' ')
        };
    }

    const record = raw as Record<string, unknown>;
    const estatus = normalizeDictamenAptitud(
        record.estatus ?? record.dictamen ?? record.dictamen_actual
    );

    return {
        estatus,
        etiqueta: String(record.etiqueta ?? record.label ?? '').trim() || estatus.replace(/_/g, ' '),
        fecha_emision:
            (record.fecha_emision as string | null | undefined) ??
            (record.fecha as string | null | undefined) ??
            null,
        fecha_vencimiento:
            (record.fecha_vencimiento as string | null | undefined) ??
            (record.vigente_hasta as string | null | undefined) ??
            null,
        restricciones_laborales:
            (record.restricciones_laborales as string | null | undefined) ?? null
    };
};

const normalizeEvento = (raw: unknown): EventoExpediente => {
    const evento = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const estatus = normalizeDictamenAptitud(
        evento.estatus_aptitud ?? evento.dictamen ?? evento.dictamen_actual
    );
    const documentoId =
        (evento.documento_id as string | null | undefined) ??
        extractCertificadoIdFromPath(evento.documento_descarga_ruta as string | null | undefined);

    return {
        id: String(evento.id ?? ''),
        tipo: String(evento.tipo ?? evento.subtipo ?? evento.tipo_evento ?? 'especial'),
        etiqueta_tipo: String(
            evento.etiqueta_tipo ??
                evento.tipo ??
                evento.subtipo ??
                evento.tipo_evento ??
                'Evaluación'
        ),
        fecha: String(evento.fecha ?? ''),
        dictamen: String(evento.dictamen ?? estatus),
        estatus_aptitud: estatus,
        tiene_documento: Boolean(evento.tiene_documento ?? documentoId),
        documento_id: documentoId,
        notas:
            (evento.notas as string | null | undefined) ??
            (evento.recomendaciones as string | null | undefined) ??
            (evento.restricciones_laborales as string | null | undefined) ??
            null
    };
};

const normalizeExpediente = (raw: unknown): ExpedienteLaboralResponse => {
    const payload = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const empleadoRaw =
        payload.empleado && typeof payload.empleado === 'object'
            ? (payload.empleado as Record<string, unknown>)
            : {};

    return {
        empleado: {
            id: String(empleadoRaw.id ?? ''),
            nombre: String(empleadoRaw.nombre ?? ''),
            apellidos: String(empleadoRaw.apellidos ?? ''),
            numero_empleado:
                (empleadoRaw.numero_empleado as string | null | undefined) ??
                (empleadoRaw.id_empleado_empresa as string | null | undefined) ??
                null,
            id_nomina:
                (empleadoRaw.id_nomina as string | null | undefined) ??
                (empleadoRaw.id_empleado_empresa as string | null | undefined) ??
                null,
            puesto: (empleadoRaw.puesto as string | null | undefined) ?? null,
            departamento:
                (empleadoRaw.departamento as string | null | undefined) ??
                (empleadoRaw.area_departamento as string | null | undefined) ??
                null
        },
        dictamen_actual: normalizeDictamenActual(payload.dictamen_actual),
        eventos: Array.isArray(payload.eventos) ? payload.eventos.map(normalizeEvento) : []
    };
};

const normalizeEstudio = (raw: unknown): EstudioParaclinico => {
    const estudio = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const estatus = normalizeDictamenAptitud(
        estudio.estatus_aptitud ?? estudio.dictamen_estudio ?? estudio.dictamen
    );

    return {
        id: String(estudio.id ?? ''),
        nombre: String(estudio.nombre ?? estudio.nombre_estudio ?? 'Estudio'),
        categoria: String(estudio.categoria ?? 'general'),
        fecha_realizacion: String(estudio.fecha_realizacion ?? ''),
        dictamen: String(estudio.dictamen ?? estudio.dictamen_estudio ?? estatus),
        estatus_aptitud: estatus,
        recomendaciones: (estudio.recomendaciones as string | null | undefined) ?? null,
        descargable: Boolean(estudio.descargable)
    };
};

const toFrontPortalPath = (ruta?: string | null, empleadoId?: string | null): string | null => {
    if (!ruta) {
        return null;
    }

    if (ruta.startsWith('/portal-cliente')) {
        return ruta;
    }

    const match = String(ruta).match(/empleados\/([^/]+)\/expediente/i);
    const id = match?.[1] || empleadoId;
    return id ? `/portal-cliente/empleados/${id}/expediente` : null;
};

const normalizeNotificacion = (raw: unknown): NotificacionPortal => {
    const item = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const empleado =
        item.empleado && typeof item.empleado === 'object'
            ? (item.empleado as Record<string, unknown>)
            : null;
    const accionRaw =
        item.accion_sugerida && typeof item.accion_sugerida === 'object'
            ? (item.accion_sugerida as Record<string, unknown>)
            : null;
    const ruta = toFrontPortalPath(
        (accionRaw?.ruta as string | null | undefined) ?? null,
        empleado?.id != null ? String(empleado.id) : null
    );

    return {
        id: String(item.id ?? ''),
        titulo: String(item.titulo ?? 'Notificación'),
        mensaje: String(item.mensaje ?? ''),
        severidad:
            item.severidad === 'urgent' || item.severidad === 'warning' || item.severidad === 'info'
                ? item.severidad
                : 'info',
        fecha_generacion: String(item.fecha_generacion ?? item.created_at ?? ''),
        leida: Boolean(item.leida ?? item.leida_at),
        accion_sugerida:
            ruta
                ? {
                      etiqueta: String(accionRaw?.etiqueta ?? 'Ver expediente'),
                      ruta
                  }
                : null
    };
};

const buildEmpleadosQuery = (params: EmpleadosListParams = {}) => {
    const query: Record<string, string | number | boolean> = {};

    if (params.search?.trim()) {
        query.search = params.search.trim();
    }

    if (params.estatus_laboral) {
        query.estatus_laboral = params.estatus_laboral;
    }

    if (params.dictamen_actual) {
        query.dictamen_actual = toApiDictamen(params.dictamen_actual);
    }

    if (params.con_restriccion_activa === true || params.con_restriccion_activa === false) {
        query.con_restriccion_activa = params.con_restriccion_activa;
    }

    if (params.page) {
        query.page = params.page;
    }

    if (params.limit) {
        query.limit = params.limit;
    }

    return query;
};

export const portalClienteService = {
    async getPerfil(): Promise<PortalPerfil> {
        const {data} = await portalAPI.get<PortalPerfil>('/portal-cliente/perfil');
        return data;
    },

    async updatePerfil(payload: PortalPerfilUpdatePayload): Promise<PortalPerfil> {
        const {data} = await portalAPI.put<PortalPerfil>('/portal-cliente/perfil', payload);
        return data;
    },

    async getDashboardResumen(): Promise<DashboardResumen> {
        const {data} = await portalAPI.get<unknown>('/portal-cliente/dashboard/resumen');
        return normalizeDashboardResumen(data);
    },

    async getExamenesPeriodicosAlertas(ventanaDias = 30): Promise<{
        proximos: Array<{empleado_id: string}>;
        vencidos: Array<{empleado_id: string}>;
    }> {
        const {data} = await portalAPI.get<{
            proximos?: Array<{empleado_id: string}>;
            vencidos?: Array<{empleado_id: string}>;
        }>('/portal-cliente/dashboard/examenes-periodicos/alertas', {
            params: {ventana_dias: ventanaDias}
        });

        return {
            proximos: Array.isArray(data?.proximos) ? data.proximos : [],
            vencidos: Array.isArray(data?.vencidos) ? data.vencidos : []
        };
    },

    async getAusentismoSeries(params?: {
        desde?: string;
        hasta?: string;
        agrupacion?: 'mes' | 'semana';
    }): Promise<AusentismoSeriesResponse> {
        const {data} = await portalAPI.get<{series?: unknown[]}>('/portal-cliente/dashboard/ausentismo/series', {
            params
        });

        const series = Array.isArray(data?.series)
            ? data.series
                  .filter(item => item && typeof item === 'object')
                  .map(item => {
                      const point = item as Record<string, unknown>;
                      return {
                          periodo: String(point.periodo ?? ''),
                          dias_ausentismo: Number(point.dias_ausentismo) || 0,
                          tasa_porcentaje: Number(point.tasa_porcentaje) || 0
                      };
                  })
            : [];

        return {series};
    },

    async listCertificados(params: CertificadosListParams = {}): Promise<CertificadosListResponse> {
        const {data} = await portalAPI.get<{
            data?: unknown[];
            pagination?: CertificadosListResponse['pagination'];
        }>('/portal-cliente/certificados-aptitud', {params});

        return {
            data: Array.isArray(data?.data) ? data.data.map(normalizeCertificado) : [],
            pagination: data?.pagination || {page: 1, limit: 20, total: 0, total_pages: 0}
        };
    },

    async listEmpleados(params: EmpleadosListParams = {}): Promise<EmpleadosListResponse> {
        const {data} = await portalAPI.get<{
            data?: unknown[];
            pagination?: EmpleadosListResponse['pagination'];
        }>('/portal-cliente/empleados', {
            params: buildEmpleadosQuery(params)
        });

        return {
            data: Array.isArray(data?.data) ? data.data.map(normalizeEmpleado) : [],
            pagination: data?.pagination || {page: 1, limit: 15, total: 0, total_pages: 0}
        };
    },

    async getExpedienteLaboral(empleadoId: string): Promise<ExpedienteLaboralResponse> {
        const {data} = await portalAPI.get<unknown>(
            `/portal-cliente/empleados/${empleadoId}/expediente-laboral`
        );
        return normalizeExpediente(data);
    },

    async listEstudiosParaclinicos(
        empleadoId: string,
        params: EstudiosParaclinicosParams = {}
    ): Promise<EstudiosParaclinicosResponse> {
        const {data} = await portalAPI.get<{
            data?: unknown[];
            pagination?: EstudiosParaclinicosResponse['pagination'];
        }>(`/portal-cliente/empleados/${empleadoId}/estudios`, {params});

        return {
            data: Array.isArray(data?.data) ? data.data.map(normalizeEstudio) : [],
            pagination: data?.pagination || {page: 1, limit: 10, total: 0, total_pages: 0}
        };
    },

    async downloadEstudioReporte(estudioId: string): Promise<Blob> {
        const {data} = await portalAPI.get<Blob>(
            `/portal-cliente/estudios/${estudioId}/download`,
            {responseType: 'blob'}
        );
        return data;
    },

    async downloadEventoExpediente(_empleadoId: string, eventoId: string): Promise<Blob> {
        // Los eventos descargables del timeline son certificados de aptitud.
        return this.downloadCertificadoAptitud(eventoId);
    },

    async getNotificaciones(params?: {
        solo_no_leidas?: boolean;
        severidad?: string;
        limit?: number;
        page?: number;
    }): Promise<NotificacionesPortalResponse> {
        const {data} = await portalAPI.get<{
            data?: unknown[];
            no_leidas?: number;
            resumen?: {no_leidas?: number};
        }>('/portal-cliente/notificaciones', {
            params
        });

        const items = Array.isArray(data?.data) ? data.data.map(normalizeNotificacion) : [];

        return {
            data: items,
            no_leidas: Number(data?.no_leidas ?? data?.resumen?.no_leidas ?? 0) || 0
        };
    },

    async markNotificacionLeida(notificacionId: string): Promise<void> {
        await portalAPI.patch(`/portal-cliente/notificaciones/${notificacionId}/leida`);
    },

    async markTodasNotificacionesLeidas(): Promise<void> {
        await portalAPI.patch('/portal-cliente/notificaciones/marcar-todas-leidas');
    },

    async listAgendaConceptos(): Promise<Array<{id: number; nombre: string; descripcion?: string | null}>> {
        const {data} = await portalAPI.get<{data?: Array<{id: number; nombre: string; descripcion?: string | null}>}>(
            '/portal-cliente/agenda/conceptos'
        );
        return Array.isArray(data?.data) ? data.data : [];
    },

    async listAgendaHorarios(fecha: string) {
        const {data} = await portalAPI.get('/portal-cliente/agenda/horarios', {
            params: {fecha}
        });
        return data as {
            fecha: string;
            horario: {apertura: string; cierre: string};
            slots: Array<{
                hora_inicio: string;
                hora_fin: string;
                label: string;
                disponible: boolean;
                motivo?: string | null;
            }>;
            mensaje?: string;
        };
    },

    async listAgendaPropuestas(params: {estado?: string; page?: number; limit?: number} = {}) {
        const {data} = await portalAPI.get('/portal-cliente/agenda/propuestas', {params});
        return data as {
            data: Array<Record<string, unknown>>;
            pagination: {page: number; limit: number; total: number; total_pages: number};
        };
    },

    async createAgendaPropuesta(payload: {
        patient_id: string;
        tipo_compromiso_id: number;
        fecha: string;
        hora_inicio: string;
        notas?: string;
    }) {
        const {data} = await portalAPI.post('/portal-cliente/agenda/propuestas', payload);
        return data;
    },

    async cancelAgendaPropuesta(id: string) {
        const {data} = await portalAPI.patch(`/portal-cliente/agenda/propuestas/${id}/cancelar`);
        return data;
    },

    async downloadCertificadoAptitud(certificadoId: string): Promise<Blob> {
        const {data} = await portalAPI.get<Blob>(
            `/portal-cliente/certificados-aptitud/${certificadoId}/download`,
            {responseType: 'blob'}
        );
        return data;
    },

    /** @deprecated Usar downloadCertificadoAptitud — el backend expone la ruta global por id. */
    async downloadCertificado(_empleadoId: string, certificadoId: string): Promise<Blob> {
        return this.downloadCertificadoAptitud(certificadoId);
    }
};
